import type { PlanNode } from '@timelog/pm-shared';
import { MAX_PLAN_DEPTH } from '@timelog/pm-shared';
import type { TFunction } from 'i18next';

import { dropDepthRange, resolveDropTarget } from './dropTarget';
import type { DropDepthRange, DropResolution } from './dropTarget';
import type { FlattenResult } from './flatten';

/**
 * What a restructure drag would do, and how to say it.
 *
 * Pure on purpose: keeping the wording here is what makes it assertable, and it
 * is the seam through which the list view could adopt the same readout. The
 * words come from `pmPlanning:drop.*` through the injected `t`, so the module
 * stays free of React and of the i18n singleton.
 *
 * The rule that governs everything below: **the drop zone only seeds a depth —
 * the resolved depth decides the words.** Hovering the bottom edge of an
 * expanded parent reads as "after" but resolves to "first child of it", because
 * the next visible row is that parent's own child and there is no gap at the
 * parent's level to sit in. Labelling from the zone would lie.
 */

/** Which third of a row the pointer is in. */
export type DropZone = 'before' | 'into' | 'after';

/*
  The two kind-based refusals are gone (`no-deliverable-above`,
  `deliverable-root-only`): a drop decides a node's kind now rather than being
  refused by it, so neither condition can arise. The ceiling is the only reason
  a gap offers no depth at all.
*/
export type DropRefusal = 'into-itself' | 'too-deep';

export type DropOutcome =
  | {
      kind: 'move';
      resolution: DropResolution;
      /** The row the moved node ends up under; null at root. */
      parentId: string | null;
      /**
       * The drop changes which node this one belongs to, rather than just its
       * position among its current siblings. Only a reparent earns the heavy
       * treatment — tinting the parent of a pure reorder is noise.
       */
      reparenting: boolean;
      /** Folded target parent that must be opened, or the drop lands unseen. */
      unfoldParentId: string | null;
      /** Where it lands, in words. */
      message: string;
      /** The BR-1 consequence, when the new parent is a leaf carrying hours. */
      note: string | null;
      range: DropDepthRange;
    }
  | { kind: 'noop'; message: string }
  | { kind: 'refused'; reason: DropRefusal; message: string };

export type DescribeDropInput = {
  plan: PlanNode[];
  flat: FlattenResult;
  dragId: string;
  /** Visible row range of the dragged subtree, hoisted to pointerdown. */
  dragIndex: number;
  dragEndIndex: number;
  /** `subtreeHeight(dragNode)`, hoisted — never recompute this per frame. */
  dragHeight: number;
  targetIndex: number;
  zone: DropZone;
  /** Arrow-key depth override, already accumulated. null = follow the zone. */
  depthOverride: number | null;
  foldedIds: ReadonlySet<string>;
  /** Translates the readout (`pmPlanning:drop.*`). */
  t: TFunction<'pmPlanning'>;
};

/** Zone boundaries within a row, in px from its top. Mirrors DROP_ZONE_EDGE_PX. */
export const DROP_ZONE_EDGE_PX = 9;
export const DROP_ROW_HEIGHT_PX = 34;

export const zoneAtOffset = (offsetY: number): DropZone => {
  if (offsetY < DROP_ZONE_EDGE_PX) return 'before';
  if (offsetY >= DROP_ROW_HEIGHT_PX - DROP_ZONE_EDGE_PX) return 'after';
  return 'into';
};

/**
 * The gap band ABOVE a row's title, where a drop means "before this row".
 *
 * Deliberately small. `gapForZone` maps the row above's `after` and this row's
 * `before` to the SAME gap, so this band only has to carry the very first row —
 * everywhere else the large `after` band above it does the work.
 */
export const DROP_NEST_TOP_PX = 12;

/**
 * Where a row's title band ends, and `after` begins.
 *
 * Measured, not derived: a plan row's title occupies 8..33px from the row's top
 * whether the row is 55px (a parent) or 72px (a leaf carrying a resources
 * line). The extra height hangs entirely BELOW the title, so the nest zone is
 * anchored to the top of the row rather than centred in it.
 */
export const DROP_NEST_BOTTOM_PX = 39;

/**
 * The same three zones on a row of ANY height — the list's rows vary (the
 * resources line makes a leaf taller than a parent), so the timeline's fixed
 * 9 px cannot be reused there.
 *
 * The zones are anchored to the row's TITLE, not shared out proportionally.
 * Two rounds of pilot feedback are why:
 *
 * - a flat 30% edge grew with the row (16.5 → 21.6px) until it swallowed the
 *   title, and dropping ONTO a task's name reordered instead of nesting;
 * - capping that edge fixed nesting but left `into` owning 56% of a leaf row,
 *   and dragging a task back OUT of a nest became the hard one.
 *
 * Both symptoms come from treating a two-line row as if its meaning were spread
 * evenly down it. It is not: the title IS the task, and the resources line
 * hanging below it is detail. So `into` covers the title, and everything under
 * it means "after this task" — which is the big, easy target an outdent needs.
 */
export const zoneInRow = (offsetY: number, rowHeight: number): DropZone => {
  // Clamped so a short row keeps a real `after` band, and `top` can never meet
  // or pass `bottom` and erase the nest zone entirely.
  const bottom = Math.min(DROP_NEST_BOTTOM_PX, rowHeight - DROP_ZONE_EDGE_PX);
  const top = Math.min(DROP_NEST_TOP_PX, Math.max(0, bottom - 1));
  if (offsetY < top) return 'before';
  if (offsetY >= bottom) return 'after';
  return 'into';
};

/**
 * The zone's seed, in the terms `resolveDropTarget` already speaks: a gap in the
 * visible list plus a desired depth. `into` deliberately lands on the resolver's
 * "one deeper than the row above" branch, which is its first-child case.
 */
export const gapForZone = (
  rowIndex: number,
  rowDepth: number,
  zone: DropZone,
): { gapIndex: number; desiredDepth: number } => {
  if (zone === 'before') return { gapIndex: rowIndex, desiredDepth: rowDepth };
  if (zone === 'into') return { gapIndex: rowIndex + 1, desiredDepth: rowDepth + 1 };
  return { gapIndex: rowIndex + 1, desiredDepth: rowDepth };
};

export const describeDrop = (input: DescribeDropInput): DropOutcome => {
  const { plan, flat, dragId, dragIndex, dragEndIndex, dragHeight, targetIndex, zone, t } = input;
  const quote = (name: string) => t('drop.Quoted', { name });
  const refused = (reason: DropRefusal, message?: string): DropOutcome => ({
    kind: 'refused',
    reason,
    message:
      message ??
      (reason === 'into-itself'
        ? t('drop.IntoItself')
        : t('drop.TooDeep', { max: MAX_PLAN_DEPTH })),
  });
  const tooDeep = (depth: number) =>
    refused('too-deep', t('drop.TooDeepWouldMake', { max: MAX_PLAN_DEPTH, depth }));
  const rows = flat.rows;
  const target = rows[targetIndex];
  const dragNode = flat.index.nodeById.get(dragId);
  if (!target || !dragNode) return refused('into-itself');

  // Anything inside the travelling block is its own destination.
  if (targetIndex >= dragIndex && targetIndex <= dragEndIndex) return refused('into-itself');

  const seed = gapForZone(targetIndex, target.depth, zone);
  const range = dropDepthRange(flat, seed.gapIndex, dragHeight);

  // Only the ceiling can close a gap now, so there is one reason left.
  if (!range) {
    return tooDeep(seed.desiredDepth + dragHeight + 1);
  }

  const desired = input.depthOverride ?? seed.desiredDepth;
  const resolution = resolveDropTarget(plan, flat, dragId, seed.gapIndex, desired, dragHeight);
  if (!resolution) return refused('into-itself');

  /*
    "Become a child" that the ceiling quietly demoted to a sibling elsewhere is a
    refusal, not a silent substitution — the user aimed at a parent and would
    otherwise get something they never asked for. Only when they did not override
    the depth themselves.
  */
  if (zone === 'into' && input.depthOverride === null && resolution.depth !== target.depth + 1) {
    return tooDeep(target.depth + 2 + dragHeight);
  }

  const currentParentId = flat.index.parentById.get(dragId) ?? null;
  const siblings =
    resolution.parentId === null
      ? plan
      : (flat.index.nodeById.get(resolution.parentId)?.children ?? []);
  const currentIndex = siblings.findIndex((sibling) => sibling.id === dragId);
  if (resolution.parentId === currentParentId && currentIndex === resolution.childIndex) {
    return { kind: 'noop', message: t('drop.AlreadyHere') };
  }

  const parentNode = resolution.parentId
    ? (flat.index.nodeById.get(resolution.parentId) ?? null)
    : null;
  const parentName = parentNode ? quote(parentNode.name) : null;

  // The sibling the drop sits next to, for wording that names a landmark rather
  // than a depth number.
  const anchorBefore = siblings[resolution.childIndex - 1];
  const anchorAfter = siblings[resolution.childIndex];

  let message: string;
  if (resolution.childIndex === 0 && parentName) {
    /*
      Landing first inside a parent that already has children is genuinely both
      "inside P" and "before its current first child". Saying both names the new
      parent — which is the part that is ambiguous — without dropping the
      landmark the user was aiming at.
    */
    message = anchorAfter
      ? t('drop.InsideBefore', { parent: parentName, anchor: quote(anchorAfter.name) })
      : t('drop.InsideFirst', { parent: parentName });
  } else if (anchorBefore) {
    const anchor = quote(anchorBefore.name);
    message = parentName
      ? t('drop.AfterIn', { anchor, parent: parentName })
      : t('drop.AfterTop', { anchor });
  } else if (anchorAfter) {
    const anchor = quote(anchorAfter.name);
    message = parentName
      ? t('drop.BeforeIn', { anchor, parent: parentName })
      : t('drop.BeforeTop', { anchor });
  } else {
    message = parentName ? t('drop.Inside', { parent: parentName }) : t('drop.FirstInPlan');
  }

  if (input.depthOverride !== null) {
    const levelsOut = seed.desiredDepth - resolution.depth;
    if (levelsOut > 0) {
      message += t('drop.OutLevels', { count: levelsOut });
    }
  }

  /*
    BR-1: a leaf that gains a child stops being a leaf, so its own figures
    cannot stay. Each transfers to the moved node only if the moved node brings
    none of that kind — decided per dimension, because budget and allocations
    are independent (BR-28), so one can move while the other is dropped.
    Saying so before the drop is the only warning the app gives about this.
  */
  let note: string | null = null;
  if (parentNode && parentNode.children.length === 0) {
    const moving: string[] = [];
    const cleared: string[] = [];
    if (parentNode.estimatedHours !== undefined) {
      const label = t('drop.NoteHours', { hours: String(parentNode.estimatedHours) });
      (dragNode.estimatedHours === undefined ? moving : cleared).push(label);
    }
    if (parentNode.allocations.length > 0) {
      (dragNode.allocations.length === 0 ? moving : cleared).push(t('drop.NoteResources'));
    }
    const and = t('drop.NoteAnd');
    const parent = parentName!;
    const dragged = quote(dragNode.name);
    if (moving.length > 0 && cleared.length > 0) {
      note = t('drop.NoteMoveAndClear', {
        parent,
        moving: moving.join(and),
        dragged,
        cleared: cleared.join(and),
      });
    } else if (moving.length > 0) {
      note = t('drop.NoteMove', { parent, moving: moving.join(and), dragged });
    } else if (cleared.length > 0) {
      note = t('drop.NoteClear', { parent, cleared: cleared.join(and) });
    }
  }

  return {
    kind: 'move',
    resolution,
    parentId: resolution.parentId,
    reparenting: resolution.parentId !== currentParentId,
    unfoldParentId:
      resolution.parentId !== null && input.foldedIds.has(resolution.parentId)
        ? resolution.parentId
        : null,
    message,
    note,
    range,
  };
};
